import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './Museum.css';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import LoadingLogo from '../components/LoadingLogo';
import Navbar from '../components/Navbar';
import FunFact from '../components/FunFact';
import { Seo } from '../components/Seo';

// Add lazy loading for images but keep original styling
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Import iOS wallpaper image
import iOSWallpaper from '../assets/ios/wallpaper.png';

// Import Symbian wallpaper image
import symbianWallpaper from '../assets/symbian/wallpaper.jpeg';

// Dual Thumb Slider Component - Defined outside the main component
const DualThumbSlider = ({ min, max, value, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  
  const sliderWidth = 280;
  const thumbWidth = 16;
  const trackRef = useRef(null);
  
  const getPosition = (val) => {
    return ((val - min) / (max - min)) * (sliderWidth - thumbWidth);
  };
  
  const handleThumbMove = (e, index) => {
    if (!trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const offset = e.clientX - rect.left;
    const percentage = Math.min(Math.max(0, offset / rect.width), 1);
    const newValue = Math.round(min + percentage * (max - min));
    
    let newValues = [...localValue];
    newValues[index] = newValue;
    
    // Ensure min <= max
    if (index === 0 && newValue > newValues[1]) {
      newValues[0] = newValues[1];
    } else if (index === 1 && newValue < newValues[0]) {
      newValues[1] = newValues[0];
    }
    
    setLocalValue(newValues);
    onChange(newValues);
  };
  
  const startDrag = (index) => (e) => {
    const handleMove = (moveEvent) => {
      handleThumbMove(moveEvent, index);
    };
    
    const stopDrag = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', stopDrag);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', stopDrag);
  };
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const leftThumbPosition = getPosition(localValue[0]);
  const rightThumbPosition = getPosition(localValue[1]);
  
  return (
    <div className="dual-thumb-slider">
      <div 
        className="slider-track" 
        ref={trackRef}
        style={{ width: sliderWidth + 'px' }}
      >
        <div 
          className="slider-fill" 
          style={{ 
            left: leftThumbPosition + 'px', 
            width: (rightThumbPosition - leftThumbPosition) + 'px' 
          }}
        ></div>
        <div 
          className="slider-thumb left-thumb" 
          style={{ left: leftThumbPosition + 'px' }}
          onMouseDown={startDrag(0)}
        ></div>
        <div 
          className="slider-thumb right-thumb" 
          style={{ left: rightThumbPosition + 'px' }}
          onMouseDown={startDrag(1)}
        ></div>
      </div>
    </div>
  );
};

// Optimize carousel with a memoized PlatformCarousel component
const PlatformCarousel = React.memo(({ platforms, selected, sliderRef, settings, onLoad }) => {
  // Handle edge case: empty platforms array
  if (!platforms || platforms.length === 0) {
    return <div className="empty-carousel">No platforms available</div>;
  }

  return (
    <Slider {...settings} ref={sliderRef}>
      {platforms.map((platform, index) => {
        // Calculate if this is the selected platform
        const isSelected = selected && platform.id === selected.id;
        
        return (
          <div 
            key={platform.id} 
            className={`platform-slide ${isSelected ? 'selected' : ''}`}
            data-platform-id={platform.id}
            data-index={index}
            data-platform-code={platform.code}
          >
            <LazyLoadImage
              src={`${API_BASE_URL}${platform.img}`}
              alt={platform.nameeng || platform.namechn || "Platform Image"}
              className="platform-image"
              effect="blur"
              threshold={100}
              onLoad={onLoad}
              wrapperClassName="platform-image-wrapper"
              loading="eager"
              visibleByDefault={true}
              placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
            />
          </div>
        );
      })}
    </Slider>
  );
});

// Game Card Component for optimization - preserve original styling
const GameCard = React.memo(({ game, platformScale, onClick, windowWidth }) => {
  return (
    <div 
      className="game-card"
      style={{
        width: `${windowWidth <= 768 ? 110 : 200}px`,
        transformOrigin: 'bottom center'
      }}
      onClick={onClick}
    >
      <div 
        className="game-card-content"
        style={{
          transformOrigin: 'bottom center',
          '--initial-scale': platformScale || 1.00
        }}
      >
        <LazyLoadImage 
          src={`${API_BASE_URL}${game.cover}`}
          alt={game.titleeng || game.titlechn}
          className="game-cover"
          effect="blur"
          threshold={300}
          wrapperClassName="game-cover-wrapper"
          loading="lazy"
          placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        />
        <div className="game-titles">
          {game.titleeng ? (
            <>
              <h3 className="title-eng">{game.titleeng}</h3>
              <h4 className="title-chn">{game.titlechn}</h4>
            </>
          ) : (
            <h3 className="title-chn-only">{game.titlechn}</h3>
          )}
        </div>
      </div>
    </div>
  );
});

// New Retro Game Card Component for Windows 95 style - modified to match desktop icons
const RetroGameCard = React.memo(({ game, onClick, windowWidth }) => {
  const [isSelected, setIsSelected] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Truncate long titles for better desktop appearance
  const truncateTitle = (title, maxLength = 20) => {
    if (!title) return '';
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };
  
  const displayTitle = game.titleeng ? truncateTitle(game.titleeng) : truncateTitle(game.titlechn);
  
  // Handle single click to select
  const handleClick = (e) => {
    e.stopPropagation();
    setIsSelected(true);
  };
  
  // Handle double click to open and unselect
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsSelected(false); // Unselect the icon when double-clicked
    onClick(); // Call the original onClick handler (to open the game)
  };
  
  // Handle image load complete
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  // Handle outside click to deselect
  useEffect(() => {
    if (!isSelected) return;
    
    const handleOutsideClick = () => setIsSelected(false);
    document.addEventListener('click', handleOutsideClick);
    
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isSelected]);
  
  return (
    <div 
      className={`win95-desktop-icon game-icon ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div className="win95-icon-img">
        <LazyLoadImage
          src={`${API_BASE_URL}${game.cover}`}
          alt={displayTitle}
          effect="blur"
          threshold={300}
          loading="lazy"
          placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
          onLoad={handleImageLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
        <div className="shortcut-overlay"></div>
      </div>
      <div className="win95-icon-label">
        {displayTitle}
      </div>
    </div>
  );
});

// Windows 95 Taskbar Component
const Win95Taskbar = React.memo(({ selectedPlatform, errorDialogs, onErrorDialogClick }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);
  
  // Format time as HH:MM AM/PM
  const formattedTime = currentTime.toLocaleTimeString([], {
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true
  });
  
  return (
    <div className="win95-taskbar">
      <div className="win95-start-button">
        <div className="win95-start-logo"></div>
        <span>Start</span>
      </div>
      <div className="win95-taskbar-buttons">
        {errorDialogs.map((dialog) => (
          <button
            key={dialog.id}
            className="win95-task-button"
            onClick={() => onErrorDialogClick(dialog.id)}
          >
            <div className="win95-error-icon"></div>
            <span>Error Starting Program</span>
          </button>
        ))}
      </div>
      <div className="win95-tray">
        <div className="win95-time">{formattedTime}</div>
      </div>
    </div>
  );
});

// Windows 95 Error Dialog Component
const Win95ErrorDialog = React.memo(({ onClose, id }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef(null);
  const overlayRef = useRef(null);

  // Center the dialog on mount
  useEffect(() => {
    const desktopContainer = document.querySelector('.retro-desktop');
    if (!desktopContainer) return;

    const desktopRect = desktopContainer.getBoundingClientRect();
    const dialogRect = dialogRef.current.getBoundingClientRect();
    
    const centerX = (desktopRect.width - dialogRect.width) / 2;
    const centerY = (desktopRect.height - dialogRect.height) / 2;
    
    setPosition({ x: centerX, y: centerY });
  }, []);

  const handleMouseDown = (e) => {
    if (e.target.closest('.win95-title-bar')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Get the bounds of the retro desktop container
      const desktopContainer = document.querySelector('.retro-desktop');
      if (!desktopContainer) return;
      
      const desktopRect = desktopContainer.getBoundingClientRect();
      const dialogRect = dialogRef.current.getBoundingClientRect();
      
      // Calculate max positions to keep dialog within bounds
      const maxX = desktopRect.width - dialogRect.width;
      const maxY = desktopRect.height - dialogRect.height;
      
      // Clamp the position within bounds
      const clampedX = Math.max(0, Math.min(newX, maxX));
      const clampedY = Math.max(0, Math.min(newY, maxY));
      
      setPosition({ x: clampedX, y: clampedY });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="win95-error-overlay" onClick={onClose} ref={overlayRef}>
      <div 
        className="win95-error-dialog" 
        onClick={(e) => e.stopPropagation()}
        ref={dialogRef}
        style={{
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseDown={handleMouseDown}
        data-dialog-id={id}
        tabIndex={0}
      >
        <div className="win95-title-bar">
          <div className="win95-title-text">Error Starting Program</div>
          <div className="win95-window-controls">
            <button className="win95-window-button win95-close-button" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="win95-error-content">
          <div className="win95-error-icon-container">
            <div className="win95-error-icon"></div>
          </div>
          <div className="win95-error-message">
            A requested .DLL file was not found.
          </div>
        </div>
        <div className="win95-error-buttons">
          <button className="win95-button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
});

// Windows 95 desktop icons
// eslint-disable-next-line no-unused-vars
const Win95DesktopIcons = React.memo(({ onSystemIconDoubleClick }) => {
  const desktopIcons = [
    { name: 'My Computer', icon: 'computer' },
    { name: 'Network Neighborhood', icon: 'network' },
    { name: 'Recycle Bin', icon: 'recycle' },
    { name: 'My Documents', icon: 'folder' },
    { name: 'The Internet', icon: 'internet' },
  ];
  
  return (
    <div className="win95-desktop-icons">
      {desktopIcons.map((icon, index) => (
        <div 
          key={index} 
          className="win95-desktop-icon"
          onDoubleClick={() => onSystemIconDoubleClick(icon.name)}
        >
          <div className={`win95-icon-img win95-icon-${icon.icon}`}></div>
          <div className="win95-icon-label">{icon.name}</div>
        </div>
      ))}
    </div>
  );
});

// Windows 95 Window Title Bar Component
// eslint-disable-next-line no-unused-vars
const Win95TitleBar = React.memo(({ title }) => {
  return (
    <div className="win95-title-bar">
      <div className="win95-title-text">{title}</div>
      <div className="win95-window-controls">
        <button className="win95-window-button win95-minimize-button">_</button>
        <button className="win95-window-button win95-maximize-button">□</button>
        <button className="win95-window-button win95-close-button">✕</button>
      </div>
    </div>
  );
});

// New iOS Game Card Component for iPad style UI
const IOSGameCard = React.memo(({ game, onClick, windowWidth, index }) => {
  const [isPressed, setIsPressed] = useState(false);
  
  // Truncate titles for better iOS appearance
  const truncateTitle = (title, maxChars = 10) => {
    if (!title) return '';
    if (title.length <= maxChars) return title;
    return title.substring(0, maxChars - 1) + '…';
  };
  
  const displayTitle = game.titleeng ? truncateTitle(game.titleeng) : truncateTitle(game.titlechn);
  
  return (
    <div 
      className="ios-app-icon"
      style={{
        '--app-index': index || 0
      }}
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      <div className={`ios-app-icon-container ${isPressed ? 'pressed' : ''}`}>
        <LazyLoadImage 
          src={`${API_BASE_URL}${game.cover}`}
          alt={game.titleeng || game.titlechn}
          className="ios-app-icon-image"
          effect="blur"
          threshold={300}
          wrapperClassName="ios-app-icon-wrapper"
          loading="lazy"
          placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        />
      </div>
      <div className="ios-app-title">
        {displayTitle}
      </div>
    </div>
  );
});

// New Modern Game Card Component
const ModernGameCard = React.memo(({ game, onClick, windowWidth }) => {
  // Truncate titles for modern style display
  const truncateTitle = (title, maxLength = 24) => {
    if (!title) return '';
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  return (
    <div 
      className="modern-game-card"
      onClick={onClick}
    >
      <div className="modern-game-card-content">
        <div className="modern-cover-container">
          <LazyLoadImage 
            src={`${API_BASE_URL}${game.cover}`}
            alt={game.titleeng || game.titlechn}
            className="modern-game-cover"
            effect="blur"
            threshold={300}
            wrapperClassName="modern-cover-wrapper"
            loading="lazy"
            placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
          />
        </div>
        <div className="modern-game-info">
          {game.titleeng ? (
            <>
              <h3 className="modern-title-eng">{truncateTitle(game.titleeng)}</h3>
              <h4 className="modern-title-chn">{truncateTitle(game.titlechn)}</h4>
            </>
          ) : (
            <h3 className="modern-title-chn-only">{truncateTitle(game.titlechn)}</h3>
          )}
        </div>
      </div>
    </div>
  );
});

// Symbian S60v3 Game Card Component
const SymbianGameCard = React.memo(({ game, onClick, windowWidth, index }) => {
  // Truncate English title for Symbian icon grid
  const truncateTitle = (title, maxChars = 12) => {
    if (!title) return '';
    if (title.length <= maxChars) return title;
    return title.substring(0, maxChars - 1) + '…';
  };
  
  const displayTitle = game.titleeng ? truncateTitle(game.titleeng) : (game.titlechn ? truncateTitle(game.titlechn) : 'Untitled');
  
  return (
    <div 
      className="symbian-app-icon"
      style={{ '--app-index': index || 0 }}
      onClick={onClick}
    >
      <div className="symbian-app-icon-container">
        <LazyLoadImage 
          src={`${API_BASE_URL}${game.cover}`}
          alt={game.titleeng || game.titlechn}
          className="symbian-app-icon-image"
          effect="blur"
          threshold={300}
          wrapperClassName="symbian-app-icon-wrapper"
          loading="lazy"
          placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        />
      </div>
      <div className="symbian-app-title">
        <span className="symbian-title-eng">{displayTitle}</span>
      </div>
    </div>
  );
});

// Symbian S60v3 Status Bar Component
const SymbianStatusBar = React.memo(() => {
  return (
    <div className="symbian-status-bar">
      <div className="symbian-status-right">
        {/* 8 horizontal bars stacked vertically - slightly wider than battery, rounded, thick, packed */}
        <svg className="symbian-battery-bars" viewBox="0 0 14 45" width="12" height="45" fill="white">
          <rect x="0" y="0" width="14" height="4" rx="1.2"/>
          <rect x="0" y="5.1" width="14" height="4" rx="1.2"/>
          <rect x="0" y="10.2" width="14" height="4" rx="1.2"/>
          <rect x="0" y="15.3" width="14" height="4" rx="1.2"/>
          <rect x="0" y="20.4" width="14" height="4" rx="1.2"/>
          <rect x="0" y="25.5" width="14" height="4" rx="1.2"/>
          <rect x="0" y="30.6" width="14" height="4" rx="1.2"/>
          <rect x="0" y="35.7" width="14" height="4" rx="1.2"/>
        </svg>
        {/* Empty battery icon facing up (vertical) - below bars */}
        <svg className="symbian-battery-icon" viewBox="0 0 12 18" width="8" height="12" fill="none" style={{ marginTop: '-5px' }}>
          <rect x="3.5" y="0" width="5" height="2" rx="0.8" fill="white"/>
          <rect x="0.5" y="2" width="11" height="15.5" rx="1.2" stroke="white" strokeWidth="1" fill="none"/>
        </svg>
      </div>
    </div>
  );
});

// Symbian S60v3 Softkey Bar Component
const SymbianSoftkeyBar = React.memo(() => {
  return (
    <div className="symbian-softkey-bar">
      <div className="symbian-softkey-left">Options</div>
      <div className="symbian-softkey-right">Exit</div>
    </div>
  );
});

// Platform Timeline Component
const PlatformTimeline = React.memo(({ items, windowWidth }) => {
  // All hooks must be called before any conditional returns
  const timelineRef = useRef(null);
  const [lineWidth, setLineWidth] = useState(0);

  useEffect(() => {
    const updateLineWidth = () => {
      const el = timelineRef.current;
      if (!el) return;
      // Measure content width based on child items (exclude the line itself)
      let maxRight = 0;
      const children = Array.from(el.children || []);
      for (const child of children) {
        if (child.classList && child.classList.contains('museum-timeline-line')) continue;
        const left = child.offsetLeft || 0;
        const width = child.offsetWidth || 0;
        const rightEdge = left + width;
        if (rightEdge > maxRight) maxRight = rightEdge;
      }
      setLineWidth(maxRight);
    };
    updateLineWidth();
    window.addEventListener('resize', updateLineWidth);
    return () => window.removeEventListener('resize', updateLineWidth);
  }, [items, windowWidth]);

  // Early return after all hooks
  if (!items || items.length === 0) return null;

  const isMobile = windowWidth <= 768;

  return (
    <div className="museum-timeline-container" aria-label="平台发展时间线">
      <div className={`museum-timeline ${isMobile ? 'vertical' : 'horizontal'}`} role="list" ref={timelineRef}>
        {!isMobile && (
          <div
            className="museum-timeline-line"
            aria-hidden="true"
            style={{ width: lineWidth ? `${lineWidth}px` : '100%' }}
          ></div>
        )}
        {items.map((it, idx) => (
          <div key={`${it.year}-${idx}`} className="museum-timeline-item" role="listitem">
            <div className="museum-timeline-dot" aria-hidden="true"></div>
            {it.year && <div className="museum-timeline-year-outside">{it.year}</div>}
            <div className="museum-timeline-card">
              <div className="museum-timeline-header">
                {isMobile && it.year && (
                  <span className="museum-timeline-year">{it.year}</span>
                )}
                {it.title && <h4 className="museum-timeline-title">{it.title}</h4>}
              </div>
              {it.description && (
                <div className="museum-timeline-description">
                  {it.description}
                </div>
              )}
              {it.image && (
                <div className="museum-timeline-image">
                  <LazyLoadImage
                    src={`${API_BASE_URL}${it.image}`}
                    alt={it.title || `事件于${it.year}`}
                    effect="blur"
                    threshold={200}
                    loading="lazy"
                    placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

// iOS Status Bar Component
const IOSStatusBar = React.memo(() => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, []);
  
  // Format time as HH:MM
  const formattedTime = currentTime.toLocaleTimeString([], {
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false
  });
  
  return (
    <div className="ios-status-bar">
      <div className="ios-status-left">
        <div className="ios-carrier">iPad</div>
        <div className="ios-wifi-icon">
          <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="15" height="15">
            <path d="M0 352.832l93.12 98.752c231.296-245.44 606.464-245.44 837.76 0L1024 352.832C741.44 53.056 283.008 53.056 0 352.832z m372.352 395.008L512 896l139.648-148.16c-76.8-81.92-202.048-81.92-279.296 0zM186.24 550.4l93.12 98.752c128.448-136.32 336.96-136.32 465.408 0L837.824 550.4c-179.648-190.592-471.488-190.592-651.648 0z" fill="#FFFFFF" />
          </svg>
        </div>
      </div>
      <div className="ios-status-center">
        <div className="ios-time">{formattedTime}</div>
      </div>
      <div className="ios-status-right">
        <div className="ios-battery-icon">
          <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="36" height="18">
            <path d="M938.667 426.667v-85.334c0-46.933-38.4-85.333-85.334-85.333H128c-46.933 0-85.333 38.4-85.333 85.333v341.334C42.667 729.6 81.067 768 128 768h725.333c46.934 0 85.334-38.4 85.334-85.333v-85.334c23.466 0 42.666-19.2 42.666-42.666v-85.334c0-23.466-19.2-42.666-42.666-42.666zM896 469.333v213.334c0 23.466-19.2 42.666-42.667 42.666H128c-23.467 0-42.667-19.2-42.667-42.666V341.333c0-23.466 19.2-42.666 42.667-42.666h725.333c23.467 0 42.667 19.2 42.667 42.666v128zM128 682.667h554.667V341.333H128v341.334z" fill="#FFFFFF" />
          </svg>
        </div>
      </div>
    </div>
  );
});

// System desktop icon component with selection behavior
const SystemDesktopIcon = React.memo(({ name, iconClass, onDoubleClick }) => {
  const [isSelected, setIsSelected] = useState(false);
  
  // Handle single click to select
  const handleClick = (e) => {
    e.stopPropagation();
    setIsSelected(true);
  };
  
  // Handle double click to trigger action and unselect
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setIsSelected(false); // Unselect the icon when double-clicked
    onDoubleClick(); // Call the original onDoubleClick handler
  };
  
  // Handle outside click to deselect
  useEffect(() => {
    if (!isSelected) return;
    
    const handleOutsideClick = () => setIsSelected(false);
    document.addEventListener('click', handleOutsideClick);
    
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isSelected]);
  
  return (
    <div 
      className={`win95-desktop-icon ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div className={`win95-icon-img ${iconClass}`}></div>
      <div className="win95-icon-label">{name}</div>
    </div>
  );
});

const Museum = () => {
  const { platformId } = useParams();
  const [games, setGames] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('games');
  const [libraryMode, setLibraryMode] = useState('current'); // 'current' or 'favorites'
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [activeSearchQuery, setActiveSearchQuery] = useState(() => new URLSearchParams(window.location.search).get('q') || ''); // The actual query used for filtering
  const [filteredGames, setFilteredGames] = useState([]);
  const [favoriteGames, setFavoriteGames] = useState([]);
  const [visiblePlatforms, setVisiblePlatforms] = useState([]);
  const [romVersion, setRomVersion] = useState('默认');
  // Filter states
  const [isPlatformFilterOpen, setIsPlatformFilterOpen] = useState(false);
  const [isYearFilterOpen, setIsYearFilterOpen] = useState(false);
  const [selectedPlatformFilters, setSelectedPlatformFilters] = useState([]);
  const [yearRange, setYearRange] = useState([1970, 2025]);
  const [minYear, setMinYear] = useState(1970);
  const [maxYear, setMaxYear] = useState(2025);
  // Add sort state
  const [isSortMethodOpen, setIsSortMethodOpen] = useState(false);
  const [sortMethod, setSortMethod] = useState('浏览量'); // Default to views sorting
  const [randomSeed, setRandomSeed] = useState(Math.random());
  const [isReshuffleSpinning, setIsReshuffleSpinning] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  
  const sliderRef = useRef(null);
  const searchInputRef = useRef(null);
  const platformDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const sortMethodDropdownRef = useRef(null);
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const location = useLocation();

  // Split loading tracking between platforms and games
  // eslint-disable-next-line no-unused-vars
  const [platformImagesLoaded, setPlatformImagesLoaded] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [platformsLoaded, setPlatformsLoaded] = useState(false);

  // Parse selected platform timeline safely
  const timelineItems = useMemo(() => {
    if (!selectedPlatform || !selectedPlatform.timeline) return [];
    try {
      const raw = selectedPlatform.timeline;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(parsed)) return [];
      // Normalize fields and sort by year if numeric
      const normalized = parsed.map((it) => ({
        year: it.year || '',
        title: it.title || '',
        description: it.description || '',
        image: it.image || ''
      }));
      return normalized.sort((a, b) => {
        const ay = parseInt(a.year, 10);
        const by = parseInt(b.year, 10);
        if (isNaN(ay) && isNaN(by)) return 0;
        if (isNaN(ay)) return 1;
        if (isNaN(by)) return -1;
        return ay - by;
      });
    } catch (e) {
      console.warn('Failed to parse platform timeline', e);
      return [];
    }
  }, [selectedPlatform]);

  // Track carousel wrap-around to fix infinite scrolling issues
  const carouselWrapTracker = useRef({ 
    isWrapping: false,
    prevIndex: -1,
    currentDirection: null,
    isRepositioning: false,  // New flag to prevent repositioning loops
    lastRepositionTime: 0    // Timestamp for throttling
  });

  // Add state for error dialog
  const [errorDialogs, setErrorDialogs] = useState([]);
  const [nextDialogId, setNextDialogId] = useState(1);
  
  // State for currently selected game's platform
  const [gameDetailsPlatform, setGameDetailsPlatform] = useState(null);
  
  // Add state for game tags
  const [gameTags, setGameTags] = useState([]);
  
  // Favorites management functions
  const getFavoritesFromStorage = () => {
    try {
      const stored = localStorage.getItem('rgm_favorite_games');
      return stored ? JSON.parse(stored) : []; // Start with empty favorites
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return []; // Fallback to empty array
    }
  };

  const saveFavoritesToStorage = (favoriteIds) => {
    try {
      localStorage.setItem('rgm_favorite_games', JSON.stringify(favoriteIds));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  };

  const [favoriteGameIds, setFavoriteGameIds] = useState(getFavoritesFromStorage);

  const toggleFavorite = useCallback((gameId) => {
    setFavoriteGameIds(prevIds => {
      const newIds = prevIds.includes(gameId) 
        ? prevIds.filter(id => id !== gameId)
        : [...prevIds, gameId];
      
      saveFavoritesToStorage(newIds);
      return newIds;
    });
  }, []);

  const isFavorite = useCallback((gameId) => {
    return favoriteGameIds.includes(gameId);
  }, [favoriteGameIds]);
  
  // Track if user has interacted with the carousel
  const [hasCarouselInteraction, setHasCarouselInteraction] = useState(false);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gamesRes, platformsRes] = await Promise.all([
          apiClient.get("/games"),
          apiClient.get("/platforms")
        ]);
        setGames(gamesRes.data);
        setFilteredGames(gamesRes.data);
        setPlatforms(platformsRes.data);
        setVisiblePlatforms(platformsRes.data);
        
        // Initialize platform filters with all platforms selected
        setSelectedPlatformFilters(platformsRes.data.map(p => p.id));
        
        // Find min and max years from games
        if (gamesRes.data.length > 0) {
          const yearsWithValues = gamesRes.data
            .map(game => game.year)
            .filter(year => year && !isNaN(parseInt(year)));
          
          if (yearsWithValues.length > 0) {
            const minGameYear = Math.min(...yearsWithValues.map(y => parseInt(y)));
            const maxGameYear = Math.max(...yearsWithValues.map(y => parseInt(y)));
            
            if (!isNaN(minGameYear) && !isNaN(maxGameYear)) {
              const roundedMinYear = Math.max(1970, Math.floor(minGameYear / 5) * 5);
              const roundedMaxYear = Math.min(2025, Math.ceil(maxGameYear / 5) * 5);
              
              setMinYear(roundedMinYear);
              setMaxYear(roundedMaxYear);
              setYearRange([roundedMinYear, roundedMaxYear]);
            }
          }
        }
        
        // Check for ?platform= URL parameter (supports numeric ID or platform code)
        const urlParams = new URLSearchParams(window.location.search);
        const urlPlatformParam = platformId || urlParams.get('platform');
        
        if (urlPlatformParam && platformsRes.data.length > 0) {
          // Try matching by numeric ID first, then by code
          const urlPlatform = platformsRes.data.find(
            p => p.id.toString() === urlPlatformParam || 
                 (p.code && p.code.toLowerCase() === urlPlatformParam.toLowerCase())
          );
          if (urlPlatform) {
            setSelectedPlatform(urlPlatform);
          } else {
            // Fallback to localStorage or first platform
            const savedPlatformId = localStorage.getItem('rgmuseum_selected_platform_id');
            const savedPlatform = savedPlatformId && platformsRes.data.find(p => p.id.toString() === savedPlatformId);
            setSelectedPlatform(savedPlatform || platformsRes.data[0]);
          }
        } else {
          // Check if we have a saved platform ID in localStorage
          const savedPlatformId = localStorage.getItem('rgmuseum_selected_platform_id');
          
          if (savedPlatformId && platformsRes.data.length > 0) {
            // Find the platform with the saved ID
            const savedPlatform = platformsRes.data.find(p => p.id.toString() === savedPlatformId);
            
            // If found, set it as selected, otherwise default to first platform
            if (savedPlatform) {
              setSelectedPlatform(savedPlatform);
            } else {
              setSelectedPlatform(platformsRes.data[0]);
            }
          } else if (platformsRes.data.length > 0) {
            // No saved platform, default to first one
            setSelectedPlatform(platformsRes.data[0]);
          }
        }
      } catch (error) {
        console.log(error);
      }
    }
    fetchData();

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    let timeoutId = null;
    const debouncedResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [platformId]);

  // Update favorite games when games data or favoriteGameIds change
  useEffect(() => {
    if (games.length > 0) {
      const favGames = games.filter(game => favoriteGameIds.includes(game.id));
      setFavoriteGames(favGames);
    }
  }, [games, favoriteGameIds]);

  // Handle clicks outside of dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(event.target)) {
        setIsPlatformFilterOpen(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setIsYearFilterOpen(false);
      }
      if (sortMethodDropdownRef.current && !sortMethodDropdownRef.current.contains(event.target)) {
        setIsSortMethodOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Maintain focus on the search input
  useEffect(() => {
    if (searchInputRef.current && document.activeElement === searchInputRef.current) {
      // Set a tiny timeout to ensure focus happens after React completes rendering
      const timeoutId = setTimeout(() => {
        searchInputRef.current.focus();
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [activeSearchQuery, filteredGames, visiblePlatforms]);

  // Effect to handle filtering and ensure UI consistency
  useEffect(() => {
    // Combine search term with platform and year filters
    const applyFilters = () => {
      // Start with all games or only favorites depending on library mode
      const baseGames = libraryMode === 'favorites' ? favoriteGames : games;
      let filtered = [...baseGames];
      
      // Apply search term filter if exists
      if (activeSearchQuery.trim()) {
        const term = activeSearchQuery.toLowerCase();
        filtered = filtered.filter(game => 
          (game.titleeng && game.titleeng.toLowerCase().includes(term)) || 
          (game.titlechn && game.titlechn.toLowerCase().includes(term))
        );
      }
      
      // Apply platform filters
      if (selectedPlatformFilters.length > 0 && selectedPlatformFilters.length < platforms.length) {
        filtered = filtered.filter(game => selectedPlatformFilters.includes(parseInt(game.platform)));
      }
      
      // Apply year range filter
      filtered = filtered.filter(game => {
        // If year range is at default values (min to max), include all games regardless of year
        if (yearRange[0] === minYear && yearRange[1] === maxYear) {
          return true;
        }
        
        // If game has no year and filter is active, exclude it
        if (!game.year || isNaN(parseInt(game.year))) {
          return false;
        }
        
        // Filter games with valid years based on the range
        const gameYear = parseInt(game.year);
        return gameYear >= yearRange[0] && gameYear <= yearRange[1];
      });
      
      // Removed sorting logic since we now handle it in the platformGames useMemo
      
      return filtered;
    };
    
    const delayDebounceFn = setTimeout(() => {
      // Apply all filters
      const filtered = applyFilters();
      setFilteredGames(filtered);
      
      // When in current library mode, also update visible platforms
      if (libraryMode === 'current') {
        // Get platform codes that have matching games
        const platformsWithGames = [...new Set(filtered.map(game => parseInt(game.platform)))];

        // Filter platforms to only show those with matching games
        const validPlatforms = platforms.filter(platform => 
          platformsWithGames.includes(platform.id)
        );

        // Save current selected platform ID before updating visible platforms
        const currentSelectedPlatformId = selectedPlatform ? selectedPlatform.id : null;

        // Update visible platforms
        setVisiblePlatforms(validPlatforms);

        // Check if the current platform should remain selected
        if (currentSelectedPlatformId) {
          const platformStillVisible = validPlatforms.some(p => p.id === currentSelectedPlatformId);
          
          // If current platform is filtered out, select the first available one
          if (!platformStillVisible && validPlatforms.length > 0) {
            setSelectedPlatform(validPlatforms[0]);
          }
        } else if (validPlatforms.length > 0) {
          // If no platform is selected, select the first available one
          setSelectedPlatform(validPlatforms[0]);
        }

        // If no search term and no filters, reset to show all
        if (!activeSearchQuery.trim() && 
            selectedPlatformFilters.length === platforms.length && 
            yearRange[0] === minYear && 
            yearRange[1] === maxYear) {
          setFilteredGames(games);
          setVisiblePlatforms(platforms);
        }
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [activeSearchQuery, selectedPlatformFilters, yearRange, games, platforms, selectedPlatform, minYear, maxYear, libraryMode, favoriteGames]);

  // Filtered favorites to display in favorites library mode
  const favoriteGamesToShow = useMemo(() => {
    if (libraryMode !== 'favorites') return favoriteGames;
    if (!activeSearchQuery.trim()) return favoriteGames;

    const term = activeSearchQuery.toLowerCase();
    return favoriteGames.filter(game => 
      (game.titleeng && game.titleeng.toLowerCase().includes(term)) ||
      (game.titlechn && game.titlechn.toLowerCase().includes(term))
    );
  }, [favoriteGames, activeSearchQuery, libraryMode]);

  // Calculate optimal games per row based on screen size and platform style
  const gamesPerRow = useMemo(() => {
    const isRetroStyle = selectedPlatform?.style === 'retro';
    const isIOSStyle = selectedPlatform?.style === 'ios';
    const isSymbianStyle = selectedPlatform?.style === 'symbian';
    const containerWidth = Math.min(windowWidth - 64, 1200);
    
    if (isSymbianStyle) {
      // Symbian S60v3 style - 3 icons per row for authentic Nokia layout
      return 3;
    } else if (isIOSStyle) {
      // iOS style has fixed grid layout - 4 icons per row for authentic iPad layout
      return 4; // Changed from 5 to 4 icons per row
    } else if (isRetroStyle) {
      // Retro style has smaller icons and more per row
      const retroGameWidth = windowWidth <= 768 ? 100 : 120;
      const gap = 16;
      const maxGamesPerRow = Math.floor((containerWidth + gap) / (retroGameWidth + gap));
      return Math.max(4, maxGamesPerRow); // At least 4 icons per row for retro style
    } else {
      // Original shelf style
      const baseGameWidth = windowWidth <= 576 ? 110 : (windowWidth <= 768 ? 150 : 200);
      const gameWidth = baseGameWidth;
      const gap = windowWidth <= 576 ? 8 : (windowWidth <= 768 ? 16 : 32);
      const maxGamesPerRow = Math.floor((containerWidth + gap) / (gameWidth + gap));
      return Math.max(windowWidth <= 576 ? 3 : 2, maxGamesPerRow);
    }
  }, [windowWidth, selectedPlatform?.style]);

  // Add a new effect to save the selected platform to localStorage when it changes
  useEffect(() => {
    if (selectedPlatform && selectedPlatform.id) {
      localStorage.setItem('rgmuseum_selected_platform_id', selectedPlatform.id.toString());
    }
  }, [selectedPlatform]);

  // Create rows of games for a platform
  const createGameRows = useCallback((platformGames) => {
    const rows = [];
    
    // For retro style, we'll just put all games in a single row and let CSS flexbox handle the wrapping
    if (selectedPlatform?.style === 'retro') {
      return [platformGames]; // Return all games in a single array/row
    }
    
    // For symbian style, also use single array for CSS grid
    if (selectedPlatform?.style === 'symbian') {
      return [platformGames];
    }
    
    // For other styles, continue with the row-based approach
    for (let i = 0; i < platformGames.length; i += gamesPerRow) {
      const row = platformGames.slice(i, i + gamesPerRow);
      rows.push(row);
    }
    return rows;
  }, [gamesPerRow, selectedPlatform?.style]);

  // Get games for selected platform - This now ensures the displayed games always match the selected platform
  const platformGames = useMemo(() => {
    if (!Array.isArray(filteredGames) || !selectedPlatform) {
      return [];
    }
    
    // First filter games by platform
    let games = filteredGames.filter(game => parseInt(game.platform) === selectedPlatform.id);
    
    // Then apply sorting based on selected method
    if (sortMethod === '浏览量') {
      // Sort by views, then by ID if views are equal
      games.sort((a, b) => {
        // Handle undefined views
        const viewsA = a.views !== undefined ? a.views : 0;
        const viewsB = b.views !== undefined ? b.views : 0;
        
        // If views are equal, sort by ID
        if (viewsB === viewsA) {
          return a.id - b.id;
        }
        
        // Otherwise sort by views (descending)
        return viewsB - viewsA;
      });
    } else if (sortMethod === '首字母排序') {
      // Sort alphabetically by English title if available, otherwise by Chinese title
      games.sort((a, b) => {
        const titleA = a.titleeng ? a.titleeng.toLowerCase() : (a.titlechn ? a.titlechn : '');
        const titleB = b.titleeng ? b.titleeng.toLowerCase() : (b.titlechn ? b.titlechn : '');
        return titleA.localeCompare(titleB);
      });
    } else if (sortMethod === '随机') {
      // Create a shuffled copy of the array to avoid state mutation issues
      const shuffled = [...games];
      // Fisher-Yates shuffle algorithm with consistent seed for current sort
      const seededRandom = (i) => {
        // Use platform ID and randomSeed together to ensure consistency
        const seed = randomSeed + (selectedPlatform.id * 100);
        const x = Math.sin(i + seed) * 10000;
        return x - Math.floor(x);
      };
      
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(seededRandom(i) * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      games = shuffled;
    }

    // Keep 枫城 as the final item in the PC Classics collection, regardless of sort mode.
    if (selectedPlatform.id === 15) {
      const fengChengIndex = games.findIndex(game => parseInt(game.id) === 939);
      if (fengChengIndex !== -1) {
        const [fengCheng] = games.splice(fengChengIndex, 1);
        games.push(fengCheng);
      }
    }
    
    return games;
  }, [filteredGames, selectedPlatform, sortMethod, randomSeed]);
  
  // Create game rows based on the filtered games for the selected platform
  const gameRows = useMemo(() => {
    return createGameRows(platformGames);
  }, [platformGames, createGameRows]);

  // Handle search input change (doesn't trigger search yet)
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // If user clears the input, also clear the active search
    if (e.target.value.trim() === '') {
      setActiveSearchQuery('');
      setIsSearchDropdownOpen(false);
    }
  };

  // Handle search submission (button click or Enter key)
  const handleSearchSubmit = () => {
    setActiveSearchQuery(searchTerm);
    if (searchTerm.trim() !== '') {
      setIsSearchDropdownOpen(true);
    } else {
      setIsSearchDropdownOpen(false);
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Clear search input
  // eslint-disable-next-line no-unused-vars
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setActiveSearchQuery('');
    // Focus the input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    
    // Reset to first platform
    if (platforms.length > 0) {
      setSelectedPlatform(platforms[0]);
    }
    
    // Reset filtered games and visible platforms
    setFilteredGames(games);
    setVisiblePlatforms(platforms);
    
    // Reset all filters
    setSelectedPlatformFilters(platforms.map(p => p.id));
    setYearRange([minYear, maxYear]);
    
    // Reset slider position after a short delay
    setTimeout(() => {
      if (sliderRef.current) {
        sliderRef.current.slickGoTo(0);
      }
    }, 50);
  }, [games, platforms, minYear, maxYear]);

  // Toggle platform filter dropdown
  const togglePlatformFilter = useCallback(() => {
    setIsPlatformFilterOpen(!isPlatformFilterOpen);
    setIsYearFilterOpen(false);
  }, [isPlatformFilterOpen]);

  // Toggle year filter dropdown
  const toggleYearFilter = useCallback(() => {
    setIsYearFilterOpen(!isYearFilterOpen);
    setIsPlatformFilterOpen(false);
  }, [isYearFilterOpen]);

  // Handle platform filter selection
  const handlePlatformFilterChange = useCallback((platformId) => {
    setSelectedPlatformFilters(prev => {
      // Convert platformId to a number to ensure proper comparison
      const numericPlatformId = parseInt(platformId);
      
      if (prev.includes(numericPlatformId)) {
        return prev.filter(id => id !== numericPlatformId);
      } else {
        return [...prev, numericPlatformId];
      }
    });
  }, []);

  // Handle "Select All" for platforms
  const handleSelectAllPlatforms = useCallback(() => {
    setSelectedPlatformFilters(platforms.map(p => p.id));
  }, [platforms]);

  // Handle "Clear All" for platforms
  const handleClearAllPlatforms = useCallback(() => {
    setSelectedPlatformFilters([]);
  }, []);

  // Handle year slider change
  const handleYearRangeChange = useCallback((newRange) => {
    setYearRange(newRange);
  }, []);

  // Handle min year input change
  const handleMinYearChange = useCallback((e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= minYear && value <= yearRange[1]) {
      setYearRange([value, yearRange[1]]);
    }
  }, [yearRange, minYear]);

  // Handle max year input change
  const handleMaxYearChange = useCallback((e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value <= maxYear && value >= yearRange[0]) {
      setYearRange([yearRange[0], value]);
    }
  }, [yearRange, maxYear]);

  // Determine slidesToShow based on platform count
  const getSlidesToShow = useCallback((count) => {
    if (count <= 2) return 1;
    if (count === 3) return 1; // Show only 1 platform when 3 platforms are available
    return 3;
  }, []);

  // Handle boundary crossing in infinite mode
  const handleSlideChange = useCallback((oldIndex, newIndex) => {
    // Early return if there aren't enough platforms for meaningful tracking
    if (visiblePlatforms.length <= 1) return;
    
    const lastIndex = visiblePlatforms.length - 1;
    
    // Detect if we're wrapping from first to last or last to first
    if (Math.abs(newIndex - oldIndex) > 1) {
      // Most likely a wrap-around case
      carouselWrapTracker.current.isWrapping = true;
      carouselWrapTracker.current.prevIndex = oldIndex;
      
      // Determine direction (forward = first to last, backward = last to first)
      if (oldIndex === 0 && newIndex === lastIndex) {
        carouselWrapTracker.current.currentDirection = 'backward';
      } else if (oldIndex === lastIndex && newIndex === 0) {
        carouselWrapTracker.current.currentDirection = 'forward';
      }
    } else {
      carouselWrapTracker.current.isWrapping = false;
      carouselWrapTracker.current.currentDirection = null;
    }
  }, [visiblePlatforms]);
  
  // Simpler approach to handle carousel behavior
  const handleBeforeChange = useCallback((current, next) => {
    // If we're currently repositioning during a wrap-around, don't process this change
    if (carouselWrapTracker.current.isRepositioning) {
      return;
    }
    
    // Track for wrap-around cases
    handleSlideChange(current, next);
    
    // Only update the selected platform if it exists in the visible platforms
    if (visiblePlatforms[next]) {
      setSelectedPlatform(visiblePlatforms[next]);
    } else {
      // Fix for infinite scrolling edge cases
      // When next is out of bounds, it's likely a cloned slide in infinite mode
      const realIndex = (next + visiblePlatforms.length) % visiblePlatforms.length;
      if (visiblePlatforms[realIndex]) {
        setSelectedPlatform(visiblePlatforms[realIndex]);
      }
    }
  }, [visiblePlatforms, handleSlideChange]);

  // Update carousel index when selectedPlatform changes, with anti-flicker protection
  useEffect(() => {
    // Only run if we have a valid selectedPlatform and sliderRef
    if (selectedPlatform && sliderRef.current && visiblePlatforms.length > 0) {
      // Find the index of selected platform in visiblePlatforms
      const platformIndex = visiblePlatforms.findIndex(p => p.id === selectedPlatform.id);
      
      // If platform is found and current slide is different from desired slide
      if (platformIndex !== -1) {
        // Prevent repositioning if we're in the middle of a wrap-around or already at the right index
        if (carouselWrapTracker.current.isRepositioning) {
          return;
        }
        
        // Get the current slide index from the carousel
        const currentSlide = sliderRef.current.innerSlider.state.currentSlide;
        
        // Only reposition if the current slide is different from the desired one
        // and it's not just a difference caused by cloned slides in infinite mode
        if (currentSlide % visiblePlatforms.length !== platformIndex % visiblePlatforms.length) {
          // Set repositioning flag
          carouselWrapTracker.current.isRepositioning = true;
          
          // Use setTimeout to ensure the carousel is fully initialized
          setTimeout(() => {
            if (sliderRef.current) {
              sliderRef.current.slickGoTo(platformIndex, true); // true = don't animate
            }
            
            // Clear repositioning flag after a short delay
            setTimeout(() => {
              carouselWrapTracker.current.isRepositioning = false;
            }, 50);
          }, 50); // Small delay to ensure carousel is ready
        }
      }
    }
  }, [selectedPlatform, visiblePlatforms]); // Include both dependencies properly

  // Handle platform image load tracking
  const handlePlatformImageLoad = useCallback(() => {
    setPlatformImagesLoaded(prev => {
      const newValue = prev + 1;
      // Only count the visible platform images (3 in the carousel)
      if (newValue >= 3 && isLoading) {
        // Only consider platform images for loading state
        setTimeout(() => {
          setIsLoading(false);
          setPlatformsLoaded(true);
        }, 300);
      }
      return newValue;
    });
  }, [isLoading]);

  // Slider settings with smoother transitions
  const sliderSettings = useMemo(() => {
    // Determine if we have enough platforms for infinite mode
    const hasEnoughPlatforms = visiblePlatforms.length > 2;
    
    return {
      className: "center",
      centerMode: true,
      infinite: hasEnoughPlatforms,
      centerPadding: "60px",
      slidesToShow: getSlidesToShow(visiblePlatforms.length),
      speed: 300, // Faster speed for smoother transitions
      focusOnSelect: true,
      swipeToSlide: true,
      dots: true,
      autoplay: false,
      pauseOnHover: true,
      touchThreshold: 10, // More forgiving touch gestures
      waitForAnimate: false, // Don't force wait for animation completion
      preventDefaultTouchMove: true, // Prevent default touch behavior for smoother experience
      beforeChange: handleBeforeChange,
      // Enhanced afterChange handler to fix boundary cases
      afterChange: (current) => {
        if (!sliderRef.current || !visiblePlatforms.length) return;

        // Mark that user has interacted with the carousel
        setHasCarouselInteraction(true);

        // Prevent repositioning if we're already in the middle of one
        if (carouselWrapTracker.current.isRepositioning) {
          return;
        }

        // Handle edge cases for infinite scrolling
        const actualIndex = (current + visiblePlatforms.length) % visiblePlatforms.length;
        const targetPlatform = visiblePlatforms[actualIndex];
        
        // Check for wrap-around cases
        if (carouselWrapTracker.current.isWrapping) {
          // Clear the wrapping state
          carouselWrapTracker.current.isWrapping = false;
          
          // Implement throttling to prevent rapid consecutive repositions
          const now = Date.now();
          const timeSinceLastReposition = now - carouselWrapTracker.current.lastRepositionTime;
          
          // Only reposition if enough time has passed (300ms)
          if (timeSinceLastReposition > 300) {
            // Set repositioning flags
            carouselWrapTracker.current.isRepositioning = true;
            carouselWrapTracker.current.lastRepositionTime = now;
            
            // Force a recentering after a brief delay
            setTimeout(() => {
              if (sliderRef.current && targetPlatform) {
                // Go to the actual slide without animation for smooth transition
                sliderRef.current.slickGoTo(actualIndex, true);
                // Update selected platform
                setSelectedPlatform(targetPlatform);
                
                // Clear repositioning flag after a short delay
                setTimeout(() => {
                  carouselWrapTracker.current.isRepositioning = false;
                }, 50);
              } else {
                carouselWrapTracker.current.isRepositioning = false;
              }
            }, 10);
          }
          return;
        }
        
        // Normal case - update selected platform if needed
        if (targetPlatform && (!selectedPlatform || selectedPlatform.id !== targetPlatform.id)) {
          setSelectedPlatform(targetPlatform);
        }
      },
      cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)', // Smoother easing function
      lazyLoad: 'ondemand', // Add native lazy loading for slick carousel
      // Force initialSlide to be a valid index and normalize slides per view
      initialSlide: selectedPlatform ? 
                    Math.max(0, visiblePlatforms.findIndex(p => selectedPlatform.id === p.id)) : 0,
      responsive: [
        {
          breakpoint: 1400,
          settings: {
            slidesToShow: getSlidesToShow(visiblePlatforms.length),
            centerPadding: "40px",
            infinite: hasEnoughPlatforms
          }
        },
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: getSlidesToShow(visiblePlatforms.length),
            centerPadding: "30px",
            infinite: hasEnoughPlatforms
          }
        },
        {
          breakpoint: 992,
          settings: {
            slidesToShow: visiblePlatforms.length === 3 ? 1 : (visiblePlatforms.length <= 2 ? 1 : 3),
            centerPadding: "20px",
            infinite: hasEnoughPlatforms
          }
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1,
            centerPadding: "60px",
            infinite: hasEnoughPlatforms
          }
        },
        {
          breakpoint: 576,
          settings: {
            slidesToShow: 1,
            centerPadding: "30px",
            infinite: hasEnoughPlatforms
          }
        }
      ]
    };
  }, [visiblePlatforms, getSlidesToShow, handleBeforeChange, selectedPlatform, carouselWrapTracker]);

  // Check if any filters are active
  const isFiltering = useMemo(() => {
    return activeSearchQuery.trim() !== '' || 
           selectedPlatformFilters.length !== platforms.length ||
           yearRange[0] !== minYear ||
           yearRange[1] !== maxYear;
  }, [activeSearchQuery, selectedPlatformFilters, platforms, yearRange, minYear, maxYear]);

  // Function to get ROM versions for a game
  const getRomVersions = useCallback((game) => {
    if (!game) return [];
    
    try {
      // Check if game.rom is a JSON string
      if (typeof game.rom === 'string' && game.rom.startsWith('[')) {
        const romData = JSON.parse(game.rom);
        if (Array.isArray(romData)) {
          return romData;
        }
      }
    } catch (e) {
      console.error('Error parsing ROM JSON:', e);
    }
    
    // Fallback to legacy format
    const versions = [];
    if (game.rom) versions.push({ type: '默认', path: game.rom });
    if (game.jpnrom) versions.push({ type: '日版', path: game.jpnrom });
    if (game.chnrom) versions.push({ type: '简中版', path: game.chnrom });
    
    return versions;
  }, []);

  // Handle play button click with version selection
  const handlePlayGame = (game, version) => {
    // Close the modal
    setSelectedGame(null);
    
    // Get available ROM versions
    const romVersions = getRomVersions(game);
    
    // Only add version parameter if multiple versions exist
    if (romVersions && romVersions.length > 1) {
      navigate(`/game/${game.id}?version=${version}`);
    } else {
    navigate(`/game/${game.id}`);
    }
  };

  // Get platform info for a game
  const findPlatformForGame = useCallback((game) => {
    if (!game || !platforms || platforms.length === 0) return null;
    return platforms.find(p => p.id === parseInt(game.platform)) || null;
  }, [platforms]);
  
  // Update game details platform whenever selected game changes
  useEffect(() => {
    if (selectedGame) {
      const platform = findPlatformForGame(selectedGame);
      setGameDetailsPlatform(platform);
      
      // Set the romVersion to the first available version for this game
      const availableVersions = getRomVersions(selectedGame);
      if (availableVersions && availableVersions.length > 0) {
        setRomVersion(availableVersions[0].type);
      } else {
        // Fallback to default if no versions are available
        setRomVersion('默认');
      }
    } else {
      setGameDetailsPlatform(null);
    }
  }, [selectedGame, findPlatformForGame, getRomVersions]);

  // Toggle sort method dropdown
  const toggleSortMethod = useCallback(() => {
    setIsSortMethodOpen(!isSortMethodOpen);
    setIsPlatformFilterOpen(false);
    setIsYearFilterOpen(false);
  }, [isSortMethodOpen]);

  // Handle reshuffle button click with animation
  const handleReshuffle = useCallback(() => {
    // Set spinning state to true to trigger animation
    setIsReshuffleSpinning(true);
    
    // Generate new random seed for shuffling
    setRandomSeed(Math.random());
    
    // Reset spinning state after animation completes
    setTimeout(() => {
      setIsReshuffleSpinning(false);
    }, 500); // Match the animation duration in CSS
  }, []);

  // Handle sort method selection
  const handleSortMethodChange = useCallback((method) => {
    // Generate a new random seed when switching to random sort
    if (method === '随机' && sortMethod !== '随机') {
      setRandomSeed(Math.random());
    }
    setSortMethod(method);
    setIsSortMethodOpen(false);
  }, [sortMethod]);

  // Handle system icon double click
  const handleSystemIconDoubleClick = useCallback((iconName) => {
    const newDialog = {
      id: nextDialogId,
      title: 'Error Starting Program'
    };
    setErrorDialogs(prev => [...prev, newDialog]);
    setNextDialogId(prev => prev + 1);
  }, [nextDialogId]);

  const handleErrorDialogClose = useCallback((dialogId) => {
    setErrorDialogs(prev => prev.filter(dialog => dialog.id !== dialogId));
  }, []);

  const handleErrorDialogClick = useCallback((dialogId) => {
    const dialog = document.querySelector(`[data-dialog-id="${dialogId}"]`);
    if (dialog) {
      dialog.focus();
    }
  }, []);

  // Add within the useEffect that fetches game details or in handleGameModal function
  // After setting the selected game:
  // Fetch game tags and full game data (including creator)
  useEffect(() => {
    const fetchGameData = async () => {
      if (selectedGame) {
        try {
          // Fetch tags
          const tagsRes = await apiClient.get(`/games/${selectedGame.id}/tags`);
          setGameTags(tagsRes.data);
          
          // Fetch full game data to get creator info
          const gameRes = await apiClient.get(`/games/${selectedGame.id}`);
          // Update selectedGame with the full data including creatorDisplayName
          setSelectedGame(prevGame => ({
            ...prevGame,
            ...gameRes.data
          }));
        } catch (err) {
          console.error('Error fetching game data:', err);
          setGameTags([]); // Reset to empty array on error
        }
      }
    };

    fetchGameData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame?.id]); // Use selectedGame.id to avoid infinite loop

  // Add useEffect to prevent scrolling when modals are open
  useEffect(() => {
    // When a modal is open, prevent scrolling on the body
    if (selectedGame || errorDialogs.length > 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedGame, errorDialogs]);

  return (
    <div className="museum-wrapper">
      {platformId && selectedPlatform && (
        <Seo
          title={`${selectedPlatform.namechn || selectedPlatform.nameeng}游戏在线玩与平台历史 | 怀旧游戏博物馆`}
          description={`${selectedPlatform.namechn || selectedPlatform.nameeng}${selectedPlatform.nameeng && selectedPlatform.namechn ? `（${selectedPlatform.nameeng}）` : ''}经典游戏在线游玩与平台历史档案。${(selectedPlatform.description || '').replace(/\s+/g, ' ').slice(0, 120)}`}
          canonical={`https://rgm.games/games/platform/${selectedPlatform.id}`}
          keywords={`${selectedPlatform.namechn || ''}游戏,${selectedPlatform.nameeng || ''}游戏在线玩,游戏机历史,经典游戏在线玩`}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${selectedPlatform.namechn || selectedPlatform.nameeng}游戏馆藏`,
            description: selectedPlatform.description,
            url: `https://rgm.games/games/platform/${selectedPlatform.id}`,
            inLanguage: 'zh-CN',
            isPartOf: { '@type': 'WebSite', name: '怀旧游戏博物馆', url: 'https://rgm.games' },
          }}
        />
      )}
      <h1 className="seo-visually-hidden">
        {platformId && selectedPlatform
          ? `${selectedPlatform.namechn || selectedPlatform.nameeng}游戏在线玩与平台历史`
          : '经典游戏在线玩与怀旧游戏库'}
      </h1>
      <Navbar />
      <button
        type="button"
        className="visitor-submit-game-button"
        onClick={() => navigate('/contribute-games')}
      >
        <span className="visitor-submit-game-icon">＋</span>
        <span><strong>贡献游戏</strong><small>扩充 RGM 馆藏</small></span>
      </button>
      <div className="museum-container">
        {isLoading && (
          <div className="loading-container">
            <LoadingLogo text="载入中..." />
            <FunFact />
          </div>
        )}
        
        {/* Combined Library Toggle and Search Bar */}
        <div className="library-search-container">
          {/* Library Toggle */}
          <div className="library-toggle-container">
            <div className="library-toggle">
              <button 
                className={`library-toggle-btn ${libraryMode === 'current' ? 'active' : ''}`}
                onClick={() => setLibraryMode('current')}
              >
                <svg t="1758404959889" className="toggle-icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <path d="M234.666667 128A64 64 0 0 1 298.666667 192v640A64 64 0 0 1 234.666667 896h-85.333334A64 64 0 0 1 85.333333 832v-640A64 64 0 0 1 149.333333 128h85.333334z m256 0A64 64 0 0 1 554.666667 192v640a64 64 0 0 1-64 64h-85.333334A64 64 0 0 1 341.333333 832v-640A64 64 0 0 1 405.333333 128h85.333334z m310.656 133.290667l137.130666 534.144a64 64 0 0 1-46.08 77.909333l-80.042666 20.48a64 64 0 0 1-77.909334-46.08L597.290667 313.770667a64 64 0 0 1 46.08-77.909334l80.042666-20.608a64.085333 64.085333 0 0 1 77.909334 46.08z" fill="currentColor"/>
                </svg>
                <span className="toggle-text">全部</span>
              </button>
              
              <button 
                className={`library-toggle-btn ${libraryMode === 'favorites' ? 'active' : ''}`}
                onClick={() => setLibraryMode('favorites')}
              >
                <svg t="1758404849975" className="toggle-icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                  <path d="M642.133333 349.013333l-114.816-232.661333a17.066667 17.066667 0 0 0-30.634666 0L381.866667 349.013333a17.066667 17.066667 0 0 1-12.842667 9.344l-256.725333 37.290667a17.066667 17.066667 0 0 0-9.472 29.141333L288.554667 605.866667a17.066667 17.066667 0 0 1 4.906666 15.104l-43.818666 255.744a17.066667 17.066667 0 0 0 24.746666 18.005333l229.674667-120.746667a17.066667 17.066667 0 0 1 15.872 0l229.674667 120.746667a17.066667 17.066667 0 0 0 24.746666-18.005333l-43.861333-255.744a17.066667 17.066667 0 0 1 4.906667-15.104l185.813333-181.077334a17.066667 17.066667 0 0 0-9.472-29.141333L654.976 358.4a17.066667 17.066667 0 0 1-12.842667-9.344z" fill="currentColor"/>
                </svg>
                <span className="toggle-text">收藏</span>
              </button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="search-filter-container">
            <div className="search-container">
              <div className="search-bar">
                <input
                  type="text"
                  className="search-input"
                  placeholder="搜索游戏..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  onFocus={() => activeSearchQuery.trim() !== '' && setIsSearchDropdownOpen(true)}
                  onBlur={(e) => {
                    // Only close if not clicking on a dropdown item
                    if (!e.relatedTarget || !e.relatedTarget.closest('.search-results-dropdown')) {
                      setTimeout(() => setIsSearchDropdownOpen(false), 200);
                    }
                  }}
                  ref={searchInputRef}
                />
              
              {/* Search Results Dropdown */}
              <div className={`search-results-dropdown thin-scrollbar ${isSearchDropdownOpen && filteredGames.length > 0 ? 'has-results' : ''}`}>
                {platforms.map(platform => {
                  // Filter games for this platform
                  const platformGames = filteredGames.filter(game => parseInt(game.platform) === platform.id);
                  
                  // Only render if there are games for this platform
                  if (platformGames.length === 0) return null;
                  
                  return (
                    <div key={platform.id} className="platform-search-group">
                      <div className="platform-search-header">
                        {platform.nameeng || platform.namechn || platform.id}
                      </div>
                      <div className="platform-search-games">
                        {platformGames.map(game => (
                          <div 
                            key={game.id} 
                            className="search-game-item"
                            tabIndex="0"
                            onClick={() => {
                              setSelectedGame(game);
                              setIsSearchDropdownOpen(false);
                            }}
                          >
                            <div className="search-game-info">
                              <div className="search-game-title">
                                <span className="search-game-chn">
                                  {game.titlechn || game.titleeng || ''}
                                </span>
                                {game.titleeng && game.titlechn && (
                                  <span className="search-game-eng">{game.titleeng}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Search actions container */}
              <div className="search-actions">
                {/* Search button */}
                <button 
                  className="search-button"
                  onClick={handleSearchSubmit}
                  title="搜索"
                  aria-label="搜索"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M20 20L16.5 16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Filter buttons inside search bar */}
              <div className="filter-container">
                {/* Platform Filter Dropdown */}
                <div className="filter-dropdown" ref={platformDropdownRef}>
                  <button 
                    className={`filter-button ${isPlatformFilterOpen ? 'active' : ''} ${selectedPlatformFilters.length !== platforms.length ? 'filtered' : ''}`}
                    onClick={togglePlatformFilter}
                    aria-label="平台筛选"
                  >
                    <span className="filter-label">平台</span>
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {isPlatformFilterOpen && (
                    <div className="dropdown-menu platform-menu">
                      <div className="dropdown-header">
                        <button className="select-all-btn" onClick={handleSelectAllPlatforms}>全选</button>
                        <button className="clear-all-btn" onClick={handleClearAllPlatforms}>清除</button>
                      </div>
                      <div className="dropdown-options">
                        {platforms.map(platform => (
                          <label key={platform.id} className="checkbox-option">
                            <input 
                              type="checkbox" 
                              checked={selectedPlatformFilters.includes(platform.id)} 
                              onChange={() => handlePlatformFilterChange(platform.id)}
                            />
                            <span className="checkbox-custom"></span>
                            <span className="option-text">
                              {platform.nameeng || platform.namechn || platform.id}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Year Range Filter Dropdown */}
                <div className="filter-dropdown" ref={yearDropdownRef}>
                  <button 
                    className={`filter-button ${isYearFilterOpen ? 'active' : ''} ${(yearRange[0] !== minYear || yearRange[1] !== maxYear) ? 'filtered' : ''}`}
                    onClick={toggleYearFilter}
                    aria-label="时间筛选"
                  >
                    <span className="filter-label">时间</span>
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {isYearFilterOpen && (
                    <div className="dropdown-menu year-menu">
                      <div className="year-slider-container">
                        <div className="year-inputs">
                          <input 
                            type="number" 
                            min={minYear} 
                            max={yearRange[1]}
                            value={yearRange[0]}
                            onChange={handleMinYearChange}
                            className="year-input"
                          />
                          <span className="year-separator">-</span>
                          <input 
                            type="number" 
                            min={yearRange[0]} 
                            max={maxYear}
                            value={yearRange[1]}
                            onChange={handleMaxYearChange}
                            className="year-input"
                          />
                        </div>
                        <DualThumbSlider 
                          min={minYear} 
                          max={maxYear} 
                          value={yearRange}
                          onChange={handleYearRangeChange}
                        />
                        <div className="year-labels">
                          <span>{minYear}</span>
                          <span>{maxYear}</span>
                        </div>
                        <div className="year-filter-info">
                          启用时间筛选后，年份未被档案的游戏将不会出现在结果中
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
          </div>
        </div>
        </div>

        {/* Main content area */}
        {visiblePlatforms.length > 0 || libraryMode === 'favorites' ? (
          <>

            {/* Show platform carousel only in current library mode */}
            {libraryMode === 'current' && (
              <>
                {/* Platform Carousel with optimized rendering */}
                <div className="platform-carousel">
                  <PlatformCarousel 
                    platforms={visiblePlatforms}
                    selected={selectedPlatform}
                    sliderRef={sliderRef}
                    settings={sliderSettings}
                    onLoad={handlePlatformImageLoad}
                  />
                </div>

                {/* Platform Titles with fade transition */}
                {selectedPlatform && (
                  <div className="selected-platform-info">
                    <div className="platform-title">
                      <h2>{selectedPlatform.nameeng || 'Unnamed Platform'}</h2>
                    </div>
                    {selectedPlatform.namechn && (
                      <div className="platform-name-chn">
                        {selectedPlatform.namechn}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            
            {/* Mobile swipe indicator - shown below titles and fades out after user interacts */}
            {libraryMode === 'current' && visiblePlatforms.length > 1 && (
              <div className={`mobile-swipe-indicator ${hasCarouselInteraction ? 'fade-out' : ''}`}>
                <span>滑动查看更多平台</span>
                <span className="swipe-icon">→</span>
              </div>
            )}

            {/* Tabs Selector - only show in current library mode */}
            {libraryMode === 'current' && (
              <div className="tabs-selector">
                <div 
                  className={`tab ${activeTab === 'games' ? 'active' : ''}`}
                  onClick={() => setActiveTab('games')}
                >
                  浏览游戏
                </div>
                <div 
                  className={`tab ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >
                  了解平台
                </div>
              </div>
            )}

            {/* Add Sort Dropdown here - only show in current library mode */}
            {libraryMode === 'current' && activeTab === 'games' && (
              <div className="sort-dropdown-container" ref={sortMethodDropdownRef}>
                <button 
                  className={`sort-button ${isSortMethodOpen ? 'active' : ''}`}
                  onClick={toggleSortMethod}
                  aria-label="排序方式"
                >
                  <span className="sort-label">排序: {sortMethod}</span>
                  <span className="dropdown-arrow">▼</span>
                </button>

                {sortMethod === '随机' && (
                  <button
                    className="reshuffle-button"
                    onClick={handleReshuffle}
                    aria-label="重新随机"
                  >
                    <span className={`reshuffle-icon ${isReshuffleSpinning ? 'spin-once' : ''}`}>↻</span>
                    <span className="reshuffle-label">重新洗牌</span>
                  </button>
                )}

                {isSortMethodOpen && (
                  <div className="dropdown-menu sort-menu">
                    <div className="dropdown-options thin-scrollbar">
                      {['浏览量', '首字母排序', '随机'].map(method => (
                        <div 
                          key={method}
                          className="checkbox-option"
                          onClick={() => handleSortMethodChange(method)}
                        >
                          <span className={`option-text ${method === sortMethod ? 'active' : ''}`}>
                            {method}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab Content Area - Wrap both tabs content to maintain consistent height */}
            <div className="tab-content-area">
              {/* Platform Description - Only show when info tab is active in current library mode */}
              {libraryMode === 'current' && selectedPlatform && activeTab === 'info' && (
                <div className="platform-description-container">
                  {selectedPlatform.description && (
                    <p className="platform-description">{selectedPlatform.description}</p>
                  )}
                  {timelineItems.length > 0 ? (
                    <PlatformTimeline items={timelineItems} windowWidth={windowWidth} />
                  ) : (
                    <div className="museum-timeline-empty">暂无时间轴</div>
                  )}
                </div>
              )}

              {/* Games Grid - Show based on library mode */}
              {((libraryMode === 'current' && activeTab === 'games') || libraryMode === 'favorites') && (
                <>
                  {/* Favorites Library Display */}
                  {libraryMode === 'favorites' ? (
                    favoriteGamesToShow.length > 0 ? (
                      <div className="games-grid">
                        {createGameRows(favoriteGamesToShow).map((row, rowIndex) => (
                          <div key={`favorites-row-${rowIndex}`} className="games-row">
                            {row.map((game) => (
                              <GameCard 
                                key={game.id}
                                game={game}
                                onClick={() => setSelectedGame(game)}
                                windowWidth={windowWidth}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-favorites-message">
                        <div className="no-favorites-icon">⭐</div>
                        <h3>还没有收藏的游戏</h3>
                        <p>在当前收藏库中找到喜欢的游戏，点击收藏按钮添加到这里</p>
                      </div>
                    )
                  ) : (
                    /* Current Library Display */
                    <>
                      {platformGames.length > 0 ? (
                    selectedPlatform?.style === 'ios' ? (
                      <div className="ios-device">
                        <IOSStatusBar />
                        <div className="ios-home-screen" style={{ backgroundImage: `url(${iOSWallpaper})` }}>
                          <div className="ios-app-grid">
                            {gameRows.map((row, rowIndex) => (
                              <div 
                                key={`${selectedPlatform?.id}-row-${rowIndex}`} 
                                className="ios-app-row"
                              >
                                {row.map((game, gameIndex) => (
                                  <IOSGameCard
                                    key={game.id}
                                    game={game}
                                    onClick={() => setSelectedGame(game)}
                                    windowWidth={windowWidth}
                                    index={rowIndex * gamesPerRow + gameIndex}
                                  />
                                ))}
                                {/* Add empty placeholders if row is not full */}
                                {row.length < gamesPerRow && Array(gamesPerRow - row.length).fill().map((_, i) => (
                                  <div key={`empty-${i}`} className="ios-app-icon empty"></div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : selectedPlatform?.style === '4399' ? (
                      <div className="style-4399">
                        <div className="style-4399-ranking">
                          <div className="style-4399-ranking-header">总排行</div>
                          <div className="style-4399-ranking-list">
                            {games
                              .filter(game => parseInt(game.platform) === selectedPlatform.id)
                              .sort((a, b) => (b.views || 0) - (a.views || 0))
                              .slice(0, 100)
                              .map((game, index) => (
                              <div 
                                key={game.id} 
                                className="style-4399-ranking-item"
                                onClick={() => setSelectedGame(game)}
                              >
                                <span className="style-4399-ranking-number">{index + 1}.</span>
                                <span className="style-4399-ranking-title">{game.titlechn || game.titleeng}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="style-4399-games">
                          <div className="style-4399-games-header">最新好玩小游戏列表</div>
                          <div className="style-4399-games-grid">
                            {platformGames.map(game => (
                              <div 
                                key={game.id} 
                                className="style-4399-game-card"
                                onClick={() => setSelectedGame(game)}
                              >
                                <LazyLoadImage 
                                  src={`${API_BASE_URL}${game.cover}`}
                                  alt={game.titlechn || game.titleeng}
                                  className="style-4399-game-cover"
                                  effect="blur"
                                  threshold={300}
                                  loading="lazy"
                                  placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                                />
                                <div className="style-4399-game-title">
                                  {game.titlechn || game.titleeng}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : selectedPlatform?.style === 'modern' ? (
                      <div className="modern-games-grid">
                        {platformGames.map(game => (
                          <ModernGameCard
                            key={game.id}
                            game={game}
                            onClick={() => setSelectedGame(game)}
                            windowWidth={windowWidth}
                          />
                        ))}
                      </div>
                    ) : selectedPlatform?.style === 'symbian' ? (
                      <div className="symbian-device">
                        <SymbianStatusBar />
                        <div className="symbian-home-screen" style={{ backgroundImage: `url(${symbianWallpaper})` }}>
                          <div className="symbian-app-grid">
                            {platformGames.map((game, gameIndex) => (
                              <SymbianGameCard
                                key={game.id}
                                game={game}
                                onClick={() => setSelectedGame(game)}
                                windowWidth={windowWidth}
                                index={gameIndex}
                              />
                            ))}
                          </div>
                        </div>
                        <SymbianSoftkeyBar />
                      </div>
                    ) : selectedPlatform?.style === 'retro' ? (
                      <div className="retro-desktop">
                        <div className="win95-desktop-icons">
                          {/* Default desktop icons */}
                          <SystemDesktopIcon 
                            name="My Computer" 
                            iconClass="win95-icon-computer"
                            onDoubleClick={() => handleSystemIconDoubleClick('My Computer')}
                          />
                          <SystemDesktopIcon 
                            name="Network Neighborhood" 
                            iconClass="win95-icon-network"
                            onDoubleClick={() => handleSystemIconDoubleClick('Network Neighborhood')}
                          />
                          <SystemDesktopIcon 
                            name="Recycle Bin" 
                            iconClass="win95-icon-recycle"
                            onDoubleClick={() => handleSystemIconDoubleClick('Recycle Bin')}
                          />
                          <SystemDesktopIcon 
                            name="My Documents" 
                            iconClass="win95-icon-folder"
                            onDoubleClick={() => handleSystemIconDoubleClick('My Documents')}
                          />
                          
                          {/* Game icons */}
                          {platformGames.map((game) => (
                            <RetroGameCard
                              key={game.id}
                              game={game}
                              onClick={() => setSelectedGame(game)}
                              windowWidth={windowWidth}
                            />
                          ))}
                        </div>
                        <Win95Taskbar 
                          selectedPlatform={selectedPlatform}
                          errorDialogs={errorDialogs}
                          onErrorDialogClick={handleErrorDialogClick}
                        />
                        
                        {/* Error Dialogs */}
                        {errorDialogs.map(dialog => (
                          <Win95ErrorDialog
                            key={dialog.id}
                            id={dialog.id}
                            onClose={() => handleErrorDialogClose(dialog.id)}
                            data-dialog-id={dialog.id}
                          />
                        ))}
                      </div>
                    ) : (
                      <div key={selectedPlatform?.id} className="games-grid">
                        {gameRows.map((row, rowIndex) => (
                          <div key={`${selectedPlatform?.id}-row-${rowIndex}`} className="games-row">
                            {row.map((game) => (
                              <GameCard 
                                key={game.id}
                                game={game}
                                onClick={() => setSelectedGame(game)}
                                windowWidth={windowWidth}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    )
                      ) : (
                        <div className="no-games-message">
                          <h3>该平台没有符合搜索条件的游戏</h3>
                          <p>试试调整搜索关键词或筛选条件</p>
                          <a href="/search" className="feedback-button center">
                            <span className="feedback-text">请求添加新游戏</span>
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="empty-state-message">
            {isFiltering ? (
              <>
                <h3>没有找到符合条件的游戏</h3>
                <p>试试调整搜索关键词或筛选条件</p>
                <a href="/search" className="feedback-button center">
                  <span className="feedback-text">请求添加新游戏</span>
                </a>
              </>
            ) : (
              "加载平台中..."
            )}
          </div>
        )}

        {/* Game Details Popup */}
        {selectedGame && (
          <div className="popup-overlay" onClick={() => setSelectedGame(null)}>
            <div className="popup-content" onClick={e => e.stopPropagation()}>
              <button 
                className="close-button"
                onClick={() => setSelectedGame(null)}
              >
                ×
              </button>
              <div className="popup-header">
                <div className="popup-cover">
                  <img 
                    src={`${API_BASE_URL}${selectedGame.cover}`} 
                    alt={selectedGame.titleeng || selectedGame.titlechn} 
                    loading="eager"
                  />
                </div>
                <div className="popup-titles">
                  <h2 className="popup-title-eng">{selectedGame.titleeng}</h2>
                  <h3 className="popup-title-chn">{selectedGame.titlechn}</h3>
                  <div className="popup-metadata">
                    <div className="metadata-item">
                      <span className="metadata-label">发行年份:</span> 
                      <span className="metadata-value">{selectedGame.year || '未知'}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="metadata-label">开发者:</span> 
                      <span className="metadata-value">{selectedGame.dev || '未知'}</span>
                    </div>
                    <div className="metadata-item">
                      <span className="metadata-label">发行商:</span> 
                      <span className="metadata-value">{selectedGame.publisher || '未知'}</span>
                    </div>
                    {selectedGame.translator && (
                      <div className="metadata-item">
                        <span className="metadata-label">汉化组:</span> 
                        <span className="metadata-value">{selectedGame.translator}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="popup-description">
                <p>{selectedGame.desc ? 
                  (selectedGame.desc.length > 150 ? 
                    selectedGame.desc.substring(0, 150) + '...' : 
                    selectedGame.desc) : 
                  '暂无描述'}</p>
              </div>

              {/* Mobile optimization warning */}
              {typeof window !== 'undefined' && window.innerWidth <= 600 && selectedGame.mobileoptimized === 0 && (
                <div className="mobile-warning">
                  <svg className="warning-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="warning-text">游戏未对移动端控制优化</span>
                </div>
              )}

              {(selectedGame.creatorDisplayName || selectedGame.contributor_alias || gameTags.length > 0) && (
                <div className="popup-tags">
                  <div className="popup-tags-container">
                    {selectedGame.creatorDisplayName && (
                      <span className="creator-tag">
                        <span className="creator-tag-label">收录者:</span>
                        <span className="creator-tag-name">{selectedGame.creatorDisplayName}</span>
                      </span>
                    )}
                    {selectedGame.contributor_alias && (
                      <span className="creator-tag">
                        <span className="creator-tag-label">贡献者:</span>
                        <span className="creator-tag-name">{selectedGame.contributor_alias}</span>
                      </span>
                    )}
                    {gameTags.map(tag => (
                      <span 
                        key={tag.id} 
                        className="popup-tag"
                        style={{ borderColor: tag.color || '#6c757d' }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="popup-actions">
                {/* Favorite button */}
                <button 
                  className={`popup-button museum-favorite-button ${isFavorite(selectedGame.id) ? 'favorited' : ''}`}
                  onClick={() => toggleFavorite(selectedGame.id)}
                  title={isFavorite(selectedGame.id) ? '取消收藏' : '添加到收藏'}
                >
                  <span className="favorite-icon">
                    {isFavorite(selectedGame.id) ? '★' : '☆'}
                  </span>
                  <span className="favorite-text">
                    {isFavorite(selectedGame.id) ? '已收藏' : '收藏'}
                  </span>
                </button>

                {/* Replace the Play button and version selector section */}
                {(() => {
                  const romVersions = getRomVersions(selectedGame);
                  
                  if (romVersions.length > 0) {
                    return (
                      <>
                        {/* Details button first */}
                        <button 
                          className="popup-button details-button"
                          onClick={() => {
                            setSelectedGame(null);
                            navigate(`/game-details/${selectedGame.id}`);
                          }}
                        >
                          查看详情
                        </button>
                        
                        {/* Play button second */}
                        <button 
                          className="popup-button museum-play-button"
                          onClick={() => handlePlayGame(selectedGame, romVersion)}
                        >
                          立即游玩
                        </button>
                        
                        {romVersions.length > 1 && (
                          <div className="version-selector-container">
                            <select 
                              className="rom-version-selector"
                              value={romVersion}
                              onChange={(e) => setRomVersion(e.target.value)}
                            >
                              {romVersions.map((rom, index) => (
                                <option key={index} value={rom.type}>
                                  {rom.type}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Add netplay button for EJS and jsdos games */}
                        {gameDetailsPlatform && 
                         (gameDetailsPlatform.emulator === 'ejs' || gameDetailsPlatform.emulator === 'jsdos') && 
                         selectedGame.multiplayer === 1 && (
                          <button 
                            className="popup-button museum-play-button"
                            onClick={() => {
                              // Open netplay page with game pre-selected
                              window.open(`https://netplay.rgm.games/games/select?game=${selectedGame.id}`, '_blank');
                            }}
                          >
                            新建联机房间
                          </button>
                        )}
                      </>
                    );
                  } else {
                    return (
                      <button 
                        className="popup-button details-button"
                        onClick={() => {
                          setSelectedGame(null);
                          navigate(`/game-details/${selectedGame.id}`);
                        }}
                      >
                        查看详情
                      </button>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Museum;
